import type { BeginnerExperience } from "@/lib/strengthStandards"
import type { InjuryDeclaration, InjuryStatus, InjuryZone } from "@/lib/supabase"
import { GOALS_BY_LEVEL, LEVELS, METRICS, PLACES, type LevelId } from "./content"

/**
 * Logique du diagnostic, sans React : ordre des écrans, blocs, validations, et ce qui
 * part en base. Tout ce qui peut se tromper silencieusement est ici, et vérifié par
 * `scripts/check-diagnostic.mjs`.
 */

export type Screen =
  | "intro"
  | "prenom"
  | "genre"
  | "mesures"
  | "niveau"
  | "experience"
  | "lieu"
  | "blessure"
  | "zones"
  | "etat"
  | "objectif"
  | "fin"

export type DiagnosticState = {
  screen: Screen
  firstName: string
  gender?: "male" | "female"
  age: number
  height: number
  weight: number
  levelId?: LevelId
  beginnerExperience?: BeginnerExperience
  place?: "gym" | "home"
  /** Réponse à « Tu as déjà eu une blessure ? ». */
  injury?: "non" | "oui"
  zones: InjuryZone[]
  zoneOther: string
  statuses: Partial<Record<InjuryZone, InjuryStatus>>
  injuryNote: string
  injuryConsent: boolean
  /** « Continuer sans enregistrer mes blessures ». */
  injuriesSkipped: boolean
  goalId?: string
}

export const initialDiagnosticState = (firstName = ""): DiagnosticState => ({
  screen: "intro",
  firstName,
  age: METRICS[0].initial,
  height: METRICS[1].initial,
  weight: METRICS[2].initial,
  zones: [],
  zoneOther: "",
  statuses: {},
  injuryNote: "",
  injuryConsent: false,
  injuriesSkipped: false,
})

// ── Ordre des écrans ────────────────────────────────────────────────────────
//
// Niveau AVANT lieu : un Avancé ne peut s'entraîner qu'en salle, et le lieu ne peut
// pas le savoir s'il est demandé avant (bug de l'ancien wizard : un Avancé « maison »
// n'avait aucun programme au catalogue, donc aucune séance planifiée).
export function screenSequence(s: DiagnosticState, askFirstName: boolean): Screen[] {
  const seq: Screen[] = ["intro"]
  if (askFirstName) seq.push("prenom")
  seq.push("genre", "mesures", "niveau")
  if (s.levelId === "beginner") seq.push("experience")
  seq.push("lieu", "blessure")
  if (s.injury === "oui") seq.push("zones", "etat")
  seq.push("objectif", "fin")
  return seq
}

export function nextScreen(s: DiagnosticState, askFirstName: boolean): Screen {
  const seq = screenSequence(s, askFirstName)
  const i = seq.indexOf(s.screen)
  return seq[Math.min(i + 1, seq.length - 1)]
}

export function previousScreen(s: DiagnosticState, askFirstName: boolean): Screen {
  const seq = screenSequence(s, askFirstName)
  const i = seq.indexOf(s.screen)
  return seq[Math.max(i - 1, 0)]
}

// ── Blocs et progression ────────────────────────────────────────────────────

export type BlockIndex = 0 | 1 | 2
export const BLOCK_KEYS = ["toi", "pratique", "objectif"] as const

const BLOCK_OF: Partial<Record<Screen, BlockIndex>> = {
  prenom: 0, genre: 0, mesures: 0,
  niveau: 1, experience: 1, lieu: 1, blessure: 1, zones: 1, etat: 1,
  objectif: 2,
}

export function blockOf(screen: Screen): BlockIndex | undefined {
  return BLOCK_OF[screen]
}

/** Premier écran d'un bloc — cible des liens « Modifier » de « Revoir mes réponses ». */
export function firstScreenOfBlock(block: BlockIndex, askFirstName: boolean): Screen {
  if (block === 0) return askFirstName ? "prenom" : "genre"
  if (block === 1) return "niveau"
  return "objectif"
}

/** Remplissage de chaque segment de la barre (0 à 1). */
export function blockProgress(s: DiagnosticState, askFirstName: boolean): [number, number, number] {
  const seq = screenSequence(s, askFirstName)
  const current = blockOf(s.screen)
  return ([0, 1, 2] as BlockIndex[]).map((b) => {
    if (s.screen === "fin") return 1
    if (current === undefined || b > current) return 0
    if (b < current) return 1
    const inBlock = seq.filter((k) => blockOf(k) === b)
    return (inBlock.indexOf(s.screen) + 1) / inBlock.length
  }) as [number, number, number]
}

/**
 * Bloc terminé en quittant cet écran vers l'avant ? Sert à l'événement
 * `diagnostic_block_completed` — une fois par bloc, au dernier écran du bloc.
 */
export function completesBlock(s: DiagnosticState, askFirstName: boolean): BlockIndex | undefined {
  const current = blockOf(s.screen)
  if (current === undefined) return undefined
  const next = nextScreen(s, askFirstName)
  return blockOf(next) === current ? undefined : current
}

// ── Transitions ─────────────────────────────────────────────────────────────

export type DiagnosticAction =
  | { type: "go"; screen: Screen }
  | { type: "next"; askFirstName: boolean }
  | { type: "back"; askFirstName: boolean }
  | { type: "setFirstName"; value: string }
  | { type: "setGender"; value: "male" | "female" }
  | { type: "setMetric"; key: "age" | "height" | "weight"; value: number }
  | { type: "setLevel"; value: LevelId }
  | { type: "setBeginnerExperience"; value: BeginnerExperience }
  | { type: "setPlace"; value: "gym" | "home" }
  | { type: "setInjury"; value: "non" | "oui" }
  | { type: "toggleZone"; zone: InjuryZone }
  | { type: "setZoneOther"; value: string }
  | { type: "setStatus"; zone: InjuryZone; status: InjuryStatus }
  | { type: "setInjuryNote"; value: string }
  | { type: "setInjuryConsent"; value: boolean }
  | { type: "skipInjuries" }
  | { type: "confirmInjuries" }
  | { type: "setGoal"; value: string }
  | { type: "restore"; state: DiagnosticState }

export function diagnosticReducer(s: DiagnosticState, a: DiagnosticAction): DiagnosticState {
  switch (a.type) {
    case "go":
      return { ...s, screen: a.screen }
    case "next":
      return { ...s, screen: nextScreen(s, a.askFirstName) }
    case "back":
      return { ...s, screen: previousScreen(s, a.askFirstName) }
    case "setFirstName":
      return { ...s, firstName: a.value }
    case "setGender":
      return { ...s, gender: a.value }
    case "setMetric": {
      const m = METRICS.find((x) => x.key === a.key)!
      return { ...s, [a.key]: Math.min(m.max, Math.max(m.min, Math.round(a.value))) }
    }
    case "setLevel": {
      if (a.value === s.levelId) return s
      // Les trois listes d'objectifs sont disjointes : un objectif choisi sous un autre
      // niveau désignerait un programme qui n'existe pas. Idem pour « maison » en Avancé.
      return {
        ...s,
        levelId: a.value,
        beginnerExperience: a.value === "beginner" ? s.beginnerExperience : undefined,
        goalId: undefined,
        place: a.value === "advanced" && s.place === "home" ? undefined : s.place,
      }
    }
    case "setBeginnerExperience":
      return { ...s, beginnerExperience: a.value }
    case "setPlace":
      if (a.value === "home" && s.levelId === "advanced") return s
      return { ...s, place: a.value }
    case "setInjury":
      if (a.value === "non") {
        return {
          ...s, injury: "non", zones: [], zoneOther: "", statuses: {},
          injuryNote: "", injuryConsent: false, injuriesSkipped: false,
        }
      }
      return { ...s, injury: "oui" }
    case "toggleZone": {
      const has = s.zones.includes(a.zone)
      const statuses = { ...s.statuses }
      if (has) delete statuses[a.zone]
      return {
        ...s,
        zones: has ? s.zones.filter((z) => z !== a.zone) : [...s.zones, a.zone],
        zoneOther: has && a.zone === "autre" ? "" : s.zoneOther,
        statuses,
      }
    }
    case "setZoneOther":
      return { ...s, zoneOther: a.value.slice(0, 50) }
    case "setStatus":
      return { ...s, statuses: { ...s.statuses, [a.zone]: a.status } }
    case "setInjuryNote":
      return { ...s, injuryNote: a.value.slice(0, 500) }
    case "setInjuryConsent":
      return { ...s, injuryConsent: a.value }
    case "skipInjuries":
      return { ...s, injuriesSkipped: true, injuryConsent: false }
    case "confirmInjuries":
      return { ...s, injuriesSkipped: false }
    case "setGoal":
      return { ...s, goalId: a.value }
    case "restore":
      return a.state
  }
}

// ── Validations ─────────────────────────────────────────────────────────────

/** Les zones cochées ont toutes un état. */
export function allZonesHaveStatus(s: DiagnosticState): boolean {
  return s.zones.length > 0 && s.zones.every((z) => s.statuses[z] !== undefined)
}

/** Le bouton « Continuer » des écrans à plusieurs valeurs. */
export function canContinue(s: DiagnosticState): boolean {
  switch (s.screen) {
    case "prenom":
      return s.firstName.trim().length >= 2
    case "mesures":
      return true
    case "zones":
      return s.zones.length > 0 && (!s.zones.includes("autre") || s.zoneOther.trim().length > 0)
    case "etat":
      return allZonesHaveStatus(s) && s.injuryConsent
    default:
      return false
  }
}

/** Tout ce qu'il faut pour enregistrer le diagnostic. */
export function isComplete(s: DiagnosticState): boolean {
  if (s.firstName.trim().length < 2 || !s.gender || !s.levelId || !s.place || !s.goalId) return false
  if (s.levelId === "beginner" && !s.beginnerExperience) return false
  if (s.levelId === "advanced" && s.place === "home") return false
  if (!GOALS_BY_LEVEL[s.levelId].some((g) => g.id === s.goalId)) return false
  if (!s.injury) return false
  if (s.injury === "oui" && !s.injuriesSkipped && !(allZonesHaveStatus(s) && s.injuryConsent)) return false
  return true
}

// ── Ce qui part en base ─────────────────────────────────────────────────────

/**
 * Blessures à enregistrer — `null` si rien ne doit partir : pas de blessure, accord
 * refusé (« Continuer sans enregistrer »), ou case non cochée. Sans accord, AUCUNE
 * information de blessure ne quitte l'appareil ; la base refuserait d'ailleurs.
 */
export function injuriesToSave(s: DiagnosticState): InjuryDeclaration[] | null {
  if (s.injury !== "oui" || s.injuriesSkipped || !s.injuryConsent || !allZonesHaveStatus(s)) return null
  return s.zones.map((zone) => ({
    zone,
    status: s.statuses[zone]!,
    zone_other: zone === "autre" ? s.zoneOther.trim() || null : null,
  }))
}

/** La précision libre, seulement avec l'accord. */
export function injuryNoteToSave(s: DiagnosticState): string | null {
  if (!injuriesToSave(s)) return null
  const note = s.injuryNote.trim()
  return note ? note : null
}

/**
 * La recommandation Coaching/Premium : au moins une zone qui fait encore mal ou gêne.
 * Calculée sur l'appareil — elle s'affiche même sans accord, puisque rien n'est écrit.
 * Une ancienne blessure guérie ne déclenche rien, et rien ne bloque jamais l'essai.
 */
export function hasActivePain(s: DiagnosticState): boolean {
  return s.injury === "oui" && s.zones.some((z) => s.statuses[z] === "genante")
}

/** « Intermédiaire · Construction Musculaire · en salle » — la carte de l'écran de fin. */
export function programmeLine(s: DiagnosticState): string {
  const level = LEVELS.find((l) => l.id === s.levelId)?.title ?? "—"
  const goal = s.levelId ? GOALS_BY_LEVEL[s.levelId].find((g) => g.id === s.goalId)?.title ?? "—" : "—"
  const place = PLACES.find((p) => p.id === s.place)?.short ?? "—"
  return `${level} · ${goal} · ${place}`
}

// ── Sauvegarde locale (rechargement de page, et plus tard le lot 5) ────────
//
// Les réponses sur les blessures ne sont JAMAIS gardées sur l'appareil : ce sont des
// données de santé, recueillies avant l'accord. Un rechargement dans cette partie
// ramène à la question « Tu as déjà eu une blessure ? ».

const INJURY_SCREENS: Screen[] = ["zones", "etat"]

export function toPersisted(s: DiagnosticState): DiagnosticState {
  return {
    ...s,
    screen: INJURY_SCREENS.includes(s.screen) ? "blessure" : s.screen,
    injury: undefined,
    zones: [],
    zoneOther: "",
    statuses: {},
    injuryNote: "",
    injuryConsent: false,
    injuriesSkipped: false,
  }
}

/** Relit un état sauvegardé ; `null` s'il est illisible ou d'une autre forme. */
export function fromPersisted(raw: unknown): DiagnosticState | null {
  if (!raw || typeof raw !== "object") return null
  const r = raw as Partial<DiagnosticState>
  const base = initialDiagnosticState(typeof r.firstName === "string" ? r.firstName : "")
  const screens: Screen[] = ["intro", "prenom", "genre", "mesures", "niveau", "experience", "lieu", "blessure", "objectif", "fin"]
  const num = (v: unknown, key: "age" | "height" | "weight") => {
    const m = METRICS.find((x) => x.key === key)!
    return typeof v === "number" && v >= m.min && v <= m.max ? v : m.initial
  }
  const level = LEVELS.some((l) => l.id === r.levelId) ? r.levelId : undefined
  const state: DiagnosticState = {
    ...base,
    screen: screens.includes(r.screen as Screen) ? (r.screen as Screen) : "intro",
    gender: r.gender === "male" || r.gender === "female" ? r.gender : undefined,
    age: num(r.age, "age"),
    height: num(r.height, "height"),
    weight: num(r.weight, "weight"),
    levelId: level,
    beginnerExperience: level === "beginner" ? r.beginnerExperience : undefined,
    place: r.place === "gym" || (r.place === "home" && level !== "advanced") ? r.place : undefined,
    goalId: level && GOALS_BY_LEVEL[level].some((g) => g.id === r.goalId) ? r.goalId : undefined,
  }
  // L'écran de fin suppose un diagnostic complet ; les blessures ne sont jamais
  // restaurées, donc on reprend à leur question.
  if (state.screen === "fin" || state.screen === "objectif") state.screen = "blessure"
  return state
}
